import { useCallback, useMemo, useRef, useState } from 'react';
import { addDays, isAfter, isBefore, isSameDay, subDays, differenceInCalendarDays } from 'date-fns';

import { AviaSearchParameters } from '@websky/graphql';
import { useConfig } from '../../../../context';
import { getDateWithoutTimezone } from '../../../../Engine/components/FastSearch/utils';
import { API_DATE_FORMAT, format } from '../../../../utils';
import { useMinPricesInPeriod } from './useMinPricesInPeriod';

function isAfterOrSame(date: Date, dateToCompare: Date): boolean {
	return isAfter(date, dateToCompare) || isSameDay(date, dateToCompare);
}

function isBeforeOrSame(date: Date, dateToCompare: Date): boolean {
	return isBefore(date, dateToCompare) || isSameDay(date, dateToCompare);
}

export function useWeekCalendarMinPrices({
	legId,
	searchParameters,
	skip
}: {
	legId: number;
	searchParameters: AviaSearchParameters;
	skip?: boolean;
}) {
	const { calendarDaysCount, RTMinPricesForOWFlights: isRTMinPricesForOWFlights } = useConfig()?.Engine;

	const prevLegIdRef = useRef(legId);
	const [calendarNavigationOffset, setCalendarNavigationOffset] = useState(0);

	const effectiveCalendarNavigationOffset = useMemo(() => {
		if (prevLegIdRef.current !== legId) {
			prevLegIdRef.current = legId;
			setCalendarNavigationOffset(0);

			return 0;
		}

		return calendarNavigationOffset;
	}, [legId, calendarNavigationOffset]);

	const { calendarDaysOffsetCount, daysOffset } = useMemo(
		() => ({
			calendarDaysOffsetCount: effectiveCalendarNavigationOffset,
			daysOffset: Math.floor(calendarDaysCount / 2)
		}),
		[calendarDaysCount, effectiveCalendarNavigationOffset]
	);

	const calendarParams = useMemo(() => {
		const uniqueDateSet = new Set<string>();
		let centralDate: Date;
		let startDate: Date;
		let endDate: Date;

		const segments = searchParameters.segments.map((segment, index) => {
			uniqueDateSet.add(segment.date);

			const segmentDate = getDateWithoutTimezone(segment.date);
			const startWeekSegmentDate = subDays(segmentDate, daysOffset);

			// Current segment
			if (index === legId) {
				const offsetDate = addDays(startWeekSegmentDate, calendarDaysOffsetCount);

				centralDate = addDays(offsetDate, daysOffset);
				startDate = subDays(centralDate, daysOffset);
				endDate = addDays(centralDate, daysOffset);

				return { ...segment, date: format(offsetDate, API_DATE_FORMAT) };
			}

			return { ...segment, date: format(startWeekSegmentDate, API_DATE_FORMAT) };
		});

		if (segments.length === 1 && isRTMinPricesForOWFlights) {
			const firstSegment = segments[0];

			segments.push({
				...firstSegment,
				departure: firstSegment.arrival,
				arrival: firstSegment.departure
			});

			return { centralDate, startDate, endDate, segments };
		}

		if (effectiveCalendarNavigationOffset !== 0 && uniqueDateSet.size > 1) {
			const isFirstLeg = legId === 0;
			const targetSegmentIndex = isFirstLeg ? segments.length - 1 : 0;
			const targetSegment = segments[targetSegmentIndex];

			const targetSegmentStartDate = getDateWithoutTimezone(targetSegment.date);
			const targetSegmentEndDate = addDays(targetSegmentStartDate, calendarDaysCount - 1);

			const shouldAdjustTargetSegment = isFirstLeg
				? isAfterOrSame(endDate, targetSegmentStartDate)
				: isBeforeOrSame(startDate, targetSegmentEndDate);

			if (shouldAdjustTargetSegment) {
				const diffDays = isFirstLeg
					? differenceInCalendarDays(endDate, targetSegmentStartDate) + 1
					: differenceInCalendarDays(startDate, targetSegmentEndDate) - 1;

				const diffWeeks =
					diffDays >= 0 ? Math.ceil(diffDays / calendarDaysCount) : Math.floor(diffDays / calendarDaysCount);

				const offsetDaysCount = calendarDaysCount * diffWeeks;

				segments[targetSegmentIndex] = {
					...targetSegment,
					date: format(addDays(targetSegmentStartDate, offsetDaysCount), API_DATE_FORMAT)
				};
			}
		}

		return { centralDate, startDate, endDate, segments };
	}, [
		searchParameters.segments,
		legId,
		daysOffset,
		calendarDaysCount,
		calendarDaysOffsetCount,
		effectiveCalendarNavigationOffset
	]);

	const minPrices = useMinPricesInPeriod({
		variables: {
			parameters: {
				...searchParameters,
				segments: calendarParams.segments,
				daysCount: calendarDaysCount
			}
		},
		context: { currency: searchParameters.currency },
		fetchPolicy: 'cache-and-network',
		skip: skip
	});

	const shiftCalendarPage = useCallback((direction: 'increase' | 'decrease', days: number) => {
		setCalendarNavigationOffset(prev => (direction === 'increase' ? prev + days : prev - days));
	}, []);

	return {
		...minPrices,
		calendarNavigationOffset: effectiveCalendarNavigationOffset,
		calendarParams,
		shiftCalendarPage
	};
}
